"use client"

import React, { useCallback, useMemo } from "react"
import { useRouter } from "next/router"

import {
  AppCoviShellRoute,
  CoviHeaderToggle,
  SOFIA_SIDEBAR_NAV_WIDTH_CLASS,
  SofiaSidebarNav,
} from "@/app-covi"
import { registerAuthRedirectGate } from "@/app-shell/layouts/authRedirectGate"
import { RouteOrganizationGate } from "@/app-shell/layouts/RouteOrganizationGate"
import {
  AppShellContributionsProvider as ShellContributionsProvider,
  type AppShellContributions,
  type DefaultPageHeaderContributionProps,
  type PrimaryNavItemContributionProps,
} from "@/app-shell/navigation/AppShellContributionsContext"
import { DevOrganizationSwitcherModal } from "@/app-shell/navigation/DevOrganizationSwitcherModal"
import {
  BillingStatusBannerRoute,
  useSignupCompletionRedirect,
} from "@/billing"
import { useDashboardShellState } from "@/dashboard"
import { AddWidgetButton, DashboardNavPopover } from "@/dashboard/navigation"
import { FeatureFlagsProvider, useCovalOnly } from "@/feature-flags"
import { GettingStartedWidget } from "@/home/navigation"
import { LocalWorkerIndicator } from "@/simulate"
import { WorkspaceProviderRoute } from "@/workspaces"
import { Show, useOrganization } from "@clerk/nextjs"

import { useOrgSwitcherModal } from "@/hooks/useOrgSwitcherModal"
import { useQueryParams } from "@/hooks/useQueryParams"
import { useRestrictedAccess } from "@/hooks/useRestrictedAccess"
import { useAdminAccess } from "@/utils/auth/dev-access"
import { isWidgetConfigRoute } from "@/utils/isDashboardRoute"

import { CommandPalette } from "../components/CommandPalette"
import { HOME_BASE_HREF } from "../config/siteNavigation"
import { buildAppShellNavigationItems } from "./appShellNavigationItems"
import { RichDefaultPageHeader } from "./RichDefaultPageHeader"
import { ToolCallNameDisplayFromFlag } from "./ToolCallNameDisplayFromFlag"
import { useImprovementLoopNav } from "./useImprovementLoopNav"
import { useNavigationFeatureFlagStatus } from "./useNavigationFeatureFlagStatus"
import { WorkspaceSidebarSelector } from "./WorkspaceSidebarSelector"

// Register billing's unfinished-signup redirect as the app-shell auth gate.
// Runs at module load (this route is imported by pages/_app), before
// LogInRequired first renders, so the hook the shell calls stays stable. Inverts
// the dependency: the shell no longer imports the billing module.
registerAuthRedirectGate(useSignupCompletionRedirect)

export const AppShellDomainContributionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  return (
    <>
      <Show when="signed-in">
        <RouteOrganizationGate>
          <FeatureFlagsProvider>
            <WorkspaceProviderRoute>
              <ToolCallNameDisplayFromFlag>
                <AppShellDomainContributionsInner>
                  {children}
                </AppShellDomainContributionsInner>
              </ToolCallNameDisplayFromFlag>
            </WorkspaceProviderRoute>
          </FeatureFlagsProvider>
        </RouteOrganizationGate>
      </Show>
      <Show when="signed-out">
        <FeatureFlagsProvider>{children}</FeatureFlagsProvider>
      </Show>
    </>
  )
}

const AppShellDomainContributionsInner = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const queryParams = useQueryParams()
  const { organization } = useOrganization()
  const hasAdminAccess = useAdminAccess()
  const { userEmail } = useRestrictedAccess()
  const featureFlagStatus = useNavigationFeatureFlagStatus()
  const improvementLoopNavEnabled = useImprovementLoopNav()

  const getHref = useCallback(
    (baseHref: string) => {
      return queryParams.getHref(baseHref, organization?.slug || undefined)
    },
    [organization?.slug, queryParams],
  )

  const primaryNavigationItems = useMemo(
    () =>
      buildAppShellNavigationItems({
        featureFlagStatus,
        getHref,
        hasAdminAccess,
        improvementLoopNavEnabled,
        userEmail,
      }),
    [
      featureFlagStatus,
      getHref,
      hasAdminAccess,
      improvementLoopNavEnabled,
      userEmail,
    ],
  )

  const contributions = useMemo<AppShellContributions>(
    () => ({
      primaryNavigationItems,
      renderPrimaryNavItem: (props) => {
        if (props.item.base_href !== "/dashboard") {
          return null
        }

        return <DashboardNavItemContribution {...props} />
      },
      // Sofia's own destinations and chat history hang under the sidebar's
      // Sofia entry on every page, not only inside Home: recent chats are how
      // the user gets back to a conversation from wherever they are, so they
      // stay put rather than appearing and folding away with the section. The
      // sidebar keeps the width those rows need for the same reason.
      renderPrimaryNavItemChildren: ({ item }) =>
        item.base_href === HOME_BASE_HREF ? <SofiaSidebarNav /> : null,
      primaryNavWidthClass: SOFIA_SIDEBAR_NAV_WIDTH_CLASS,
      renderSidebarFooterBeforeOrg: ({ orgSlug }) => (
        <>
          {orgSlug && <GettingStartedWidget orgSlug={orgSlug} />}
          <LocalWorkerIndicator />
        </>
      ),
      renderSidebarPrimaryBeforeNav: ({ className }) => (
        <WorkspaceSidebarSelector className={className} />
      ),
      renderDefaultPageHeader: (props) => (
        <DashboardHeaderContribution {...props} />
      ),
      renderHeaderTrailing: ({ className }) => (
        <CoviHeaderToggle className={className} />
      ),
      renderStatusBanner: ({ className }) => (
        <BillingStatusBannerRoute className={className} />
      ),
      renderCommandPalette: () => (
        <>
          <CommandPalette
            featureFlagStatus={featureFlagStatus}
            improvementLoopNavEnabled={improvementLoopNavEnabled}
          />
          <CovalOnlyOrganizationSwitcher />
        </>
      ),
    }),
    [featureFlagStatus, improvementLoopNavEnabled, primaryNavigationItems],
  )

  return (
    <ShellContributionsProvider contributions={contributions}>
      <AppCoviShellRoute>{children}</AppCoviShellRoute>
    </ShellContributionsProvider>
  )
}

const CovalOnlyOrganizationSwitcher = () => {
  const enabled = useCovalOnly()

  return enabled ? <OrganizationSwitcher /> : null
}

const OrganizationSwitcher = () => {
  const { isOpen, closeModal } = useOrgSwitcherModal()

  return <DevOrganizationSwitcherModal isOpen={isOpen} onClose={closeModal} />
}

const DashboardNavItemContribution = ({
  item,
  getHref,
  isActive,
  isOpen,
  onOpenChange,
}: PrimaryNavItemContributionProps) => {
  const {
    currentDashboardId,
    dashboardFetchError,
    dashboards,
    dashboardsLoading,
    handleNewDashboard,
    handleToggleFavorite,
  } = useDashboardShellState()

  return (
    <DashboardNavPopover
      item={item}
      dashboards={dashboards}
      isLoading={dashboardsLoading}
      fetchError={dashboardFetchError}
      getHref={getHref}
      onToggleFavorite={(id) => {
        void handleToggleFavorite(id)
      }}
      isActive={isActive}
      currentDashboardId={currentDashboardId}
      onCreateNewDashboard={(displayName) => {
        void handleNewDashboard(displayName)
      }}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    />
  )
}

const DashboardHeaderContribution = ({
  getHref,
  isDashboardPage,
}: DefaultPageHeaderContributionProps) => {
  const router = useRouter()
  const { currentDashboard, currentDashboardId, organizationSlug } =
    useDashboardShellState()

  // The widget create/edit page supplies its own header actions, so the
  // dashboard-level "New Widget" button is redundant there.
  const isWidgetConfigPage = isWidgetConfigRoute(router.pathname)

  return (
    <RichDefaultPageHeader
      getHref={getHref}
      isDashboardPage={isDashboardPage}
      dashboardDisplayName={
        isDashboardPage && currentDashboard
          ? currentDashboard.is_default
            ? "Main"
            : currentDashboard.display_name?.trim() || "Untitled"
          : undefined
      }
      dashboardHeaderActions={
        isDashboardPage &&
        !isWidgetConfigPage &&
        currentDashboardId &&
        organizationSlug ? (
          <AddWidgetButton
            dashboardId={currentDashboardId}
            organizationSlug={organizationSlug}
          />
        ) : null
      }
    />
  )
}
